import type { ConsentController } from './consent';

/**
 * The shared decision used before and after loading the optional runtimes.
 * Keep this helper out of the entry module's exports: a named export there
 * makes Rollup replace the public IIFE API with a default-export namespace.
 */
export function resolveConsentedRuntime(
  config: { consentOnly?: boolean; recorder?: { enabled?: boolean } },
  controller: Pick<ConsentController, 'canUseService'> | null
): { recorder: boolean; widget: boolean } {
  if (config.consentOnly === true) return { recorder: false, widget: false };
  return {
    recorder:
      config.recorder?.enabled !== false &&
      (!controller || controller.canUseService('perspectify_recorder')),
    widget: !controller || controller.canUseService('perspectify_feedback_widget'),
  };
}

/**
 * Why the recorder is being stopped. The reason decides what happens to
 * capture that is still sitting in the browser's buffer.
 */
export type RecorderStopReason = 'consent-withdrawn' | 'teardown' | 'identity-rotation' | 'privacy-tightened';

/**
 * Decide whether a stop may deliver what it still holds.
 *
 * `recorder.stop()` flushes the buffer before it finishes. That is right when
 * the session simply ends — a page unload or an identity rotation happens while
 * the consent grant is still valid, and dropping that capture would lose data
 * the visitor agreed to.
 *
 * It is wrong once consent is gone. A withdrawal has to stop collection *and*
 * drop what collection already produced; flushing it turns the act of opting
 * out into one last upload. Requests already in flight cannot be recalled, but
 * no new one may be started on the buffer's behalf.
 */
export function resolveRecorderStopMode(reason: RecorderStopReason): { discardBuffer: boolean } {
  return { discardBuffer: reason === 'consent-withdrawn' || reason === 'privacy-tightened' };
}
